import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { HomepageReplayCard } from "@/components/hub/HomepageReplayControls";
import { Radio, Plus, Mic, Users, MessageSquare, ArrowRight, Loader2 } from "lucide-react";

function LiveDot() {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
    </span>
  );
}

export default function HubList() {
  const { can } = useAuth();
  const canHost = can("producer_tools");
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", event_id: "", allow_anonymous: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [ss, ev] = await Promise.all([
      api.get("/hub/sessions").then((r) => r.data).catch(() => []),
      canHost ? api.get("/events").then((r) => r.data).catch(() => []) : Promise.resolve([]),
    ]);
    setSessions(ss);
    setEvents(ev);
    setLoading(false);
  }, [canHost]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title) { toast.error("Give your session a title"); return; }
    setSaving(true);
    try {
      const res = await api.post("/hub/sessions", { ...form, event_id: form.event_id || null });
      toast.success("Session created");
      navigate(`/portal/hub/${res.data.session_id}`);
    } catch (e) { toast.error("Could not create session"); } finally { setSaving(false); }
  };

  if (loading) return <SectionLoader />;

  const live = sessions.filter((s) => s.status === "live");
  const scheduled = sessions.filter((s) => s.status !== "live");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Podcast Hub</h1>
          <p className="mt-1 text-muted-foreground">Join the live conversation — ask questions, vote, and react in real time.</p>
        </div>
        {canHost && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-create-button"><Plus className="mr-2 h-4 w-4" />New session</Button>
          </div>
        )}
      </div>

      {canHost && <HomepageReplayCard />}

      {live.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2"><LiveDot /><h2 className="font-serif text-lg font-semibold text-foreground">Live now</h2></div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {live.map((s) => (
              <Card key={s.session_id} className="border-primary/40 bg-card shadow-[0_12px_30px_rgba(8,45,80,0.12)]" data-testid="hub-live-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <Badge className="border border-red-200 bg-red-50 text-red-700"><LiveDot /><span className="ml-1.5">LIVE</span></Badge>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground tabular-nums"><Users className="h-3.5 w-3.5" />{s.viewer_count}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-lg font-semibold text-foreground">{s.title}</h3>
                  {s.event && <p className="text-sm text-muted-foreground">{s.event.title}</p>}
                  <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground tabular-nums"><MessageSquare className="h-3.5 w-3.5" />{s.question_count} questions</div>
                  <Button asChild className="mt-4 w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"><Link to={`/portal/hub/${s.session_id}`}>Join live <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-3 font-serif text-lg font-semibold text-foreground">Sessions</h2>
        {scheduled.length === 0 && live.length === 0 ? (
          <EmptyState icon={Mic} title="No sessions yet" description={canHost ? "Create a podcast session to start engaging your audience live." : "When the team opens a podcast session, it'll show up here."} action={canHost ? <Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" />New session</Button> : null} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {scheduled.map((s) => (
              <Card key={s.session_id} className="border-border bg-card" data-testid="hub-session-card">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{s.status === "scheduled" ? "Scheduled" : s.status}</Badge>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground tabular-nums"><MessageSquare className="h-3.5 w-3.5" />{s.question_count}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-lg font-semibold text-foreground">{s.title}</h3>
                  {s.event && <p className="text-sm text-muted-foreground">{s.event.title}{s.event.date ? ` · ${formatDate(s.event.date)}` : ""}</p>}
                  <Button asChild variant="secondary" className="mt-4 w-full"><Link to={`/portal/hub/${s.session_id}`}>Open hub <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">New podcast session</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Friday Conversations — Live" data-testid="hub-title-input" /></div>
            <div><Label>Link to event (optional)</Label>
              <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="No event" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No event</SelectItem>{events.map((e) => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">Linking an event connects this live session to its details.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3"><div><Label>Allow anonymous questions</Label><p className="text-xs text-muted-foreground">Members can ask without showing their name.</p></div><Switch checked={form.allow_anonymous} onCheckedChange={(v) => setForm({ ...form, allow_anonymous: v })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="hub-create-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & open"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
